import { useState } from "react";

const CATEGORIES = [
  {
    label: "Frontend",
    color: "cyan",
    tools: ["React", "Next.js", "TypeScript", "Angular", "Flutter", "React Native"],
  },
  {
    label: "Backend",
    color: "purple",
    tools: ["Node.js", "Python", "FastAPI", "Django", "Java", ".NET"],
  },
  {
    label: "AI & ML",
    color: "cyan",
    tools: ["OpenAI", "LangChain", "TensorFlow", "Computer Vision", "NLP", "Predictive Analytics"],
  },
  {
    label: "Database",
    color: "purple",
    tools: ["PostgreSQL", "MongoDB", "MySQL", "Firebase", "Redis", "Oracle"],
  },
  {
    label: "Cloud & DevOps",
    color: "cyan",
    tools: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Jenkins"],
  },
  {
    label: "Hardware & 3D",
    color: "purple",
    tools: ["Arduino", "Raspberry Pi", "ESP32", "Fusion 360", "FDM / SLA", "CAD / CAM"],
  },
];

const TOOL_ICONS = {
  "React": "https://cdn.simpleicons.org/react/61DAFB",
  "Next.js": "https://cdn.simpleicons.org/nextdotjs/FFFFFF",
  "TypeScript": "https://cdn.simpleicons.org/typescript/3178C6",
  "Angular": "https://cdn.simpleicons.org/angular/DD0031",
  "Flutter": "https://cdn.simpleicons.org/flutter/02569B",
  "React Native": "https://cdn.simpleicons.org/react/61DAFB",
  "Node.js": "https://cdn.simpleicons.org/nodedotjs/5FA04E",
  "Python": "https://cdn.simpleicons.org/python/3776AB",
  "FastAPI": "https://cdn.simpleicons.org/fastapi/009688",
  "Django": "https://cdn.simpleicons.org/django/092E20",
  "Java": "https://cdn.simpleicons.org/openjdk/FFFFFF",
  ".NET": "https://cdn.simpleicons.org/dotnet/512BD4",
  "OpenAI": "https://cdn.simpleicons.org/openai/FFFFFF",
  "LangChain": "https://cdn.simpleicons.org/langchain/1C3C3C",
  "TensorFlow": "https://cdn.simpleicons.org/tensorflow/FF6F00",
  "Computer Vision": "https://cdn.simpleicons.org/opencv/5C3EE8",
  "NLP": "https://cdn.simpleicons.org/spacy/09A3D5",
  "PostgreSQL": "https://cdn.simpleicons.org/postgresql/4169E1",
  "MongoDB": "https://cdn.simpleicons.org/mongodb/47A248",
  "MySQL": "https://cdn.simpleicons.org/mysql/4479A1",
  "Firebase": "https://cdn.simpleicons.org/firebase/FFCA28",
  "Redis": "https://cdn.simpleicons.org/redis/DC382D",
  "Oracle": "https://cdn.simpleicons.org/oracle/F80000",
  "AWS": "https://cdn.simpleicons.org/amazonaws/FF9900",
  "Azure": "https://cdn.simpleicons.org/microsoftazure/0078D4",
  "Google Cloud": "https://cdn.simpleicons.org/googlecloud/4285F4",
  "Docker": "https://cdn.simpleicons.org/docker/2496ED",
  "Kubernetes": "https://cdn.simpleicons.org/kubernetes/326CE5",
  "Jenkins": "https://cdn.simpleicons.org/jenkins/D24939",
  "Arduino": "https://cdn.simpleicons.org/arduino/00878F",
  "Raspberry Pi": "https://cdn.simpleicons.org/raspberrypi/A22846",
  "ESP32": "https://cdn.simpleicons.org/esphome/000000",
  "Fusion 360": "https://cdn.simpleicons.org/autodesk/0696D7",
};

const TOOL_FALLBACK_LABELS = {
  "Computer Vision": "CV",
  "Predictive Analytics": "PA",
  "NLP": "NLP",
  "AWS": "AWS",
  "Azure": "AZ",
  "Oracle": "OR",
};

function ToolIcon({ tool, color }) {
  const [failed, setFailed] = useState(false);
  const src = TOOL_ICONS[tool];
  const fallbackLabel = TOOL_FALLBACK_LABELS[tool] || tool.slice(0, 2).toUpperCase();

  if (!src || failed) {
    return (
      <span
        className={`inline-flex h-4 min-w-4 px-1 items-center justify-center rounded text-[8px] font-mono leading-none border flex-shrink-0 ${
          color === "cyan"
            ? "border-accent-cyan/40 text-accent-cyan"
            : "border-accent-purple/40 text-accent-purple"
        }`}
        aria-hidden="true"
      >
        {fallbackLabel}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`${tool} logo`}
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-3.5 h-3.5 rounded-[2px] object-contain flex-shrink-0 opacity-90"
    />
  );
}

export default function TechStack() {
  return (
    <section
      id="tech-stack"
      className="relative py-24 md:py-32"
      data-testid="techstack-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              / 05 — Tech stack
            </div>
            <h2
              className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
              data-testid="techstack-heading"
            >
              The tools behind
              <br />
              <span className="text-zinc-500">every discipline.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-lg font-light">
            Modern, opinionated, and battle-tested — from frontend frameworks to
            hardware microcontrollers to 3D CAD. We use the right tool for every layer.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CATEGORIES.map((cat, ci) => (
            <div
              key={cat.label}
              className="glass-card rounded-2xl p-5 sm:p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300"
              data-testid={`techstack-category-${ci}`}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                e.currentTarget.style.setProperty('--x', `${x}px`);
                e.currentTarget.style.setProperty('--y', `${y}px`);
              }}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
                style={{
                  background:
                    cat.color === "cyan"
                      ? "radial-gradient(300px circle at var(--x, 50%) var(--y, 0%), rgba(0,229,255,0.1), transparent 70%)"
                      : "radial-gradient(300px circle at var(--x, 50%) var(--y, 0%), rgba(108,99,255,0.12), transparent 70%)",
                }}
              />
              <div className="relative">
                <div
                  className={`font-mono text-[10px] uppercase tracking-[0.3em] mb-4 ${
                    cat.color === "cyan" ? "text-accent-cyan" : "text-accent-purple"
                  }`}
                >
                  {cat.label}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {cat.tools.map((tool) => (
                    <div
                      key={tool}
                      data-testid={`tech-${tool.toLowerCase().replace(/[\s/.]/g, "-")}`}
                      className="group/tool flex items-center gap-2"
                    >
                      <ToolIcon tool={tool} color={cat.color} />
                      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-400 group-hover/tool:text-white transition-colors">
                        {tool}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
